'use server';

/**
 * @fileOverview An interactive chat for answering questions about the portfolio.
 *
 * - interactivePortfolioChat - A function that handles the chat interactions.
 * - InteractivePortfolioChatInput - The input type for the interactivePortfolioChat function.
 * - InteractivePortfolioChatOutput - The return type for the interactivePortfolioChat function.
 */

import {ai} from '@/ai/genkit';
import {z} from 'zod';
import { searchSimilarDocuments } from '@/ai/services/vector-search';
import { logChatConversation } from '@/ai/services/chat-logger';
import { checkRateLimit } from '@/ai/services/rate-limiter';
import { headers } from 'next/headers';

const InteractivePortfolioChatInputSchema = z.object({
  message: z.string().describe('The user message.'),
  conversationHistory: z.array(z.object({
    role: z.enum(['user', 'bot']),
    content: z.string(),
  })).optional().describe('The conversation history.'),
});
export type InteractivePortfolioChatInput = z.infer<typeof InteractivePortfolioChatInputSchema>;

const InteractivePortfolioChatOutputSchema = z.object({
  response: z.string().describe('The bot response.'),
  updatedConversationHistory: z.array(z.object({
    role: z.enum(['user', 'bot']),
    content: z.string(),
  })).describe('The updated conversation history.'),
});
export type InteractivePortfolioChatOutput = z.infer<typeof InteractivePortfolioChatOutputSchema>;

export async function interactivePortfolioChat(input: InteractivePortfolioChatInput): Promise<InteractivePortfolioChatOutput> {
  return interactivePortfolioChatFlow(input);
}

// Define a schema for the formatted conversation history passed to the prompt
const FormattedConversationEntrySchema = z.object({
  formattedText: z.string().describe('A conversation entry with role prefix.'),
});

const PromptInputSchema = InteractivePortfolioChatInputSchema.extend({
  formattedConversationHistory: z.array(FormattedConversationEntrySchema).optional(),
  ragDocuments: z.array(z.string()).optional(),
});

function buildPromptText(
  message: string,
  formattedHistory: Array<{formattedText: string}>,
  ragDocs: string[]
): string {
  const ragSection = ragDocs.map(doc => `- ${doc}`).join('\n  ');
  const historySection = formattedHistory.map(h => h.formattedText).join('\n  ');
  
  return `You are a chatbot that answers questions about Marcos Vázquez Rey's portfolio.
  Answer as if you are Marcos Vázquez Rey, the author of this portfolio page.
  Use the following information to answer the user's questions:
  
  ${ragSection}

  Your response should be informative, engaging, and in the first person.
  If the user asks a question that is not related to the portfolio or the provided information, respond with a polite message saying that I can only answer questions about my portfolio and my experience.
  Try to follow the language that the user is utilizing in their questions.

  Current conversation history:
  ${historySection}
  
  User: ${message}
  Bot: `;
}

const interactivePortfolioChatFlow = ai.defineFlow(
  {
    name: 'interactivePortfolioChatFlow',
    inputSchema: InteractivePortfolioChatInputSchema,
    outputSchema: InteractivePortfolioChatOutputSchema,
  },
  async (input: InteractivePortfolioChatInput): Promise<InteractivePortfolioChatOutput> => {
    const startTime = Date.now();
    const { message, conversationHistory = [] } = input;
    
    // Get user information for rate limiting
    let identifier = 'unknown';
    let userAgent: string | undefined;
    let userIp: string | undefined;
    
    try {
      const headersList = await headers();
      userAgent = headersList.get('user-agent') || undefined;
      const forwardedFor = headersList.get('x-forwarded-for');
      const realIp = headersList.get('x-real-ip');
      userIp = forwardedFor?.split(',')[0] || realIp || undefined;
      identifier = userIp || 'unknown';
    } catch (error) {
      console.error('Error getting headers:', error);
    }

    // Check rate limit BEFORE processing
    const rateLimitCheck = await checkRateLimit(identifier, message);
    
    if (!rateLimitCheck.allowed) {
      console.log(`🚫 Rate limit exceeded for ${identifier}: ${rateLimitCheck.reason}`);
      
      // Return error message as bot response
      const errorHistory = [
        ...conversationHistory,
        { role: 'user' as const, content: message },
        { role: 'bot' as const, content: rateLimitCheck.reason || 'Rate limit exceeded.' },
      ];
      
      return {
        response: rateLimitCheck.reason || 'Rate limit exceeded. Please try again later.',
        updatedConversationHistory: errorHistory,
      };
    }

    const historyWithUserMessage = [...conversationHistory, { role: 'user' as const, content: message }];

    const formattedConversationHistory = historyWithUserMessage.map(entry => ({
      formattedText: `${entry.role === 'user' ? 'User:' : 'Bot:'} ${entry.content}`
    }));

    let botResponse = 'I can only answer questions about my portfolio and my experience.';
    let relevantDocsCount = 0;
    
    try {
      // Step 1: Search for relevant documents using vector similarity
      console.log('🔍 Searching for relevant documents...');
      const relevantDocs = await searchSimilarDocuments(
        message,
        0.3, // Lower threshold to get more results (0.3 = 30% similarity minimum)
        5    // Get top 5 most relevant documents
      );

      relevantDocsCount = relevantDocs.length;
      console.log(`✅ Found ${relevantDocsCount} relevant documents`);
      
      // Extract the content from search results
      const relevantContent = relevantDocs.map(doc => doc.content);
      
      // Step 2: Build the prompt with only relevant documents
      const promptText = buildPromptText(message, formattedConversationHistory, relevantContent);
      
      // Step 3: Generate response using the LLM
      const result = await ai.generate({
        model: 'googleai/gemini-2.5-flash-lite',
        prompt: promptText,
        config: {
          temperature: 0.7,
        },
      });
      
      // Extract text from the result
      const generatedText = result.text;
      
      if (generatedText && generatedText.trim() !== '') {
        botResponse = generatedText.trim();
      }
    } catch (error) {
      console.error('Error generating response:', error);
      // If vector search fails, provide a helpful error message
      botResponse = 'I apologize, but I\'m having trouble accessing my knowledge base right now. Please try again in a moment.';
    }

    const updatedConversationHistory = [
      ...historyWithUserMessage,
      {role: 'bot' as const, content: botResponse},
    ];

    // Calculate response time
    const responseTimeMs = Date.now() - startTime;

    // Log the conversation to Supabase (don't await - fire and forget)
    // Use the identifier we already got for rate limiting
    try {
      // Generate a simple session ID based on IP and timestamp (or use a UUID)
      const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      logChatConversation({
        sessionId,
        userMessage: message,
        botResponse,
        userIp,
        userAgent,
        responseTimeMs,
        relevantDocsCount,
      }).catch(err => {
        console.error('Failed to log conversation:', err);
      });
    } catch (error) {
      console.error('Error logging conversation:', error);
    }

    return {
      response: botResponse,
      updatedConversationHistory,
    };
  }
);
